import React from 'react';
import {
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';
import LayersIcon from '@material-ui/icons/Layers';
import TimelineIcon from '@material-ui/icons/Timeline';
import DeleteIcon from '@material-ui/icons/Delete';
import { ArgusEnv, ArgusStack, ArgusStackEnriched } from '@eng-portal/plugin-argus-common';
import { useAsync } from 'react-use';
import { argusApiRef } from '@eng-portal/plugin-argus-common/src/argusApiRef';
import { useApi } from '@backstage/core-plugin-api';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import { StackCard } from '../Cards/StackCard';
import { ClusterCard } from '../Cards/ClusterCard';
import { Progress } from '@backstage/core-components';
import { toast } from 'react-toastify';

type StackExplorerProps = {
  appName: string;
};

// Sort environments: rdev first, then staging, then prod, then alphabetically for others
function sortEnvs(envNames: string[]): string[] {
  const priority: Record<string, number> = {
    rdev: 0,
    staging: 1,
    prod: 2,
  };

  return [...envNames].sort((a, b) => {
    const aPriority = priority[a.toLowerCase()] ?? 999;
    const bPriority = priority[b.toLowerCase()] ?? 999;
    if (aPriority !== bPriority) {
      return aPriority - bPriority;
    }
    return a.localeCompare(b);
  });
}

export function StackExplorer({ appName }: StackExplorerProps): JSX.Element {
  const argusApi = useApi(argusApiRef);
  const [searchParams, setSearchParams] = useSearchParams();
  const [envs, setEnvs] = React.useState<ArgusEnv[]>([]);
  const [envsToStacks, setEnvsToStacks] = React.useState<Record<string, ArgusStack[]>>({});
  const [expandedEnvs, setExpandedEnvs] = React.useState<Record<string, boolean>>({});
  const [selectedStack, setSelectedStack] = React.useState<ArgusStackEnriched | null>(null);
  const [selectedEnv, setSelectedEnv] = React.useState<ArgusEnv | null>(null);

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [deleteProcessing, setDeleteProcessing] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState<string | undefined>(undefined);

  // Track the current URL params as a string to detect actual URL changes
  const currentUrlKey = `${searchParams.get('env') || ''}:${searchParams.get('stack') || ''}`;
  const lastSyncedUrlKeyRef = React.useRef<string>('');

  // Load environment and stack data
  useAsync(async () => {
    const loadedEnvs = await argusApi.listEnvs(appName);
    setEnvs(loadedEnvs);
    const envsToStacksUpdate: Record<string, ArgusStack[]> = {};
    for (const env of loadedEnvs) {
      const stacks = await argusApi.listStacks(appName, env.name);
      envsToStacksUpdate[env.name] = stacks;
    }
    setEnvsToStacks(envsToStacksUpdate);
  }, [appName]);

  // Sync selection with URL params (handles initial load and browser back/forward)
  React.useEffect(() => {
    // Skip if we've already synced this URL
    if (lastSyncedUrlKeyRef.current === currentUrlKey) {
      return;
    }

    const syncSelectionFromUrl = async (): Promise<void> => {
      const envParam = searchParams.get('env');
      const stackParam = searchParams.get('stack');

      // Mark as synced
      lastSyncedUrlKeyRef.current = currentUrlKey;

      // If no params, clear selection
      if (!envParam || !stackParam) {
        setSelectedStack(null);
        setSelectedEnv(null);
        return;
      }

      // Find and select the stack from URL params
      const env = envs.find((e) => e.name === envParam);
      const stack = envsToStacks[envParam]?.find((s) => s.name === stackParam);
      if (env && stack) {
        const info = await argusApi.getStackInfo(appName, envParam, stackParam);
        setSelectedStack({ stack, info });
        setSelectedEnv(env);
        setExpandedEnvs((prev) => ({ ...prev, [envParam]: true }));
      }
    };

    if (Object.keys(envsToStacks).length > 0) {
      syncSelectionFromUrl();
    }
  }, [currentUrlKey, searchParams, envs, envsToStacks, appName, argusApi]);

  const handleEnvClick = (envName: string): void => {
    setExpandedEnvs((prev) => ({
      ...prev,
      [envName]: !prev[envName],
    }));
  };

  const handleStackClick = async (stack: ArgusStack, envName: string): Promise<void> => {
    // Mark this URL as already synced so the effect doesn't re-fetch
    const newUrlKey = `${envName}:${stack.name}`;
    lastSyncedUrlKeyRef.current = newUrlKey;

    // Fetch stack info and update selection
    const info = await argusApi.getStackInfo(appName, envName, stack.name);
    setSelectedStack({ stack, info });
    const env = envs.find((e) => e.name === envName);
    setSelectedEnv(env ?? null);

    // Update URL
    setSearchParams({ env: envName, stack: stack.name });
  };

  const handleDeleteDialogOpen = (): void => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteDialogClose = (): void => {
    if (deleteProcessing) {
      // Don't allow closing while deletion is in progress
      return;
    }
    setDeleteDialogOpen(false);
    setDeleteError(undefined);
  };

  const handleDeleteStack = async (): Promise<void> => {
    if (!selectedStack || !selectedEnv) return;

    setDeleteProcessing(true);
    setDeleteError(undefined);

    try {
      await argusApi.deleteStack(appName, selectedEnv.name, selectedStack.stack.name);

      // Remove the deleted stack from local state
      setEnvsToStacks((prev) => ({
        ...prev,
        [selectedEnv.name]: prev[selectedEnv.name].filter(
          (s) => s.name !== selectedStack.stack.name
        ),
      }));

      // Clear selection and URL params
      setSelectedStack(null);
      setSelectedEnv(null);
      setSearchParams({});
      lastSyncedUrlKeyRef.current = ':';

      // Close dialog and reset state
      setDeleteDialogOpen(false);

      // Use a timeout to prevent the dialog from resetting while it is still closing
      setTimeout(() => {
        setDeleteProcessing(false);
        setDeleteError(undefined);
      }, 500);

      toast.success(`Stack "${selectedStack.stack.name}" was successfully deleted`);
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.error('Error deleting stack:', error);
      setDeleteError(error?.message || String(error));
      setDeleteProcessing(false);
    }
  };

  const sortedEnvNames = sortEnvs(Object.keys(envsToStacks));

  return (
    <Box display="flex" style={{ height: 'calc(100vh - 280px)', minHeight: 300 }}>
      {/* Sidebar */}
      <Paper
        elevation={1}
        style={{
          width: 280,
          minWidth: 280,
          display: 'flex',
          flexDirection: 'column',
          borderRight: '1px solid rgba(0, 0, 0, 0.12)',
        }}
      >
        <Box style={{ padding: 16, borderBottom: '1px solid rgba(0, 0, 0, 0.12)', flexShrink: 0 }}>
          <Typography variant="h6">Environments</Typography>
        </Box>
        <List component="nav" disablePadding style={{ overflow: 'auto', flex: 1 }}>
          {sortedEnvNames.map((envName) => (
            <React.Fragment key={envName}>
              <ListItem button onClick={() => handleEnvClick(envName)}>
                <ListItemText
                  primary={envName}
                  primaryTypographyProps={{ style: { fontWeight: 500 } }}
                />
                {expandedEnvs[envName] ? <ExpandLess /> : <ExpandMore />}
              </ListItem>
              <Collapse in={expandedEnvs[envName]} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {[...envsToStacks[envName]].sort((a, b) => a.name.localeCompare(b.name)).map((stack) => (
                    <ListItem
                      key={stack.name}
                      button
                      style={{ paddingLeft: 32 }}
                      selected={selectedStack?.stack.name === stack.name}
                      onClick={() => handleStackClick(stack, envName)}
                    >
                      <LayersIcon style={{ marginRight: 8, fontSize: 18, opacity: 0.7 }} />
                      <ListItemText
                        primary={stack.name}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </React.Fragment>
          ))}
        </List>
      </Paper>

      {/* Main content */}
      <Box flex={1} style={{ padding: 16, paddingTop: 0, overflow: 'auto' }}>
        {selectedStack && selectedEnv ? (
          <Box display="flex" flexDirection="column" gridGap={16}>
            <Box display="flex" justifyContent="flex-end" gridGap={8}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<TimelineIcon />}
                component={RouterLink}
                to={`../argus-events?${new URLSearchParams({
                  app: appName,
                  env: selectedEnv.name,
                  stack: selectedStack.stack.name,
                  relativeRange: 'last_30_days',
                }).toString()}`}
              >
                View Events
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteDialogOpen}
              >
                Delete
              </Button>
            </Box>
            <StackCard stack={selectedStack} />
            <ClusterCard cluster={selectedEnv.cluster} />

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={handleDeleteDialogClose} maxWidth="sm" fullWidth>
              <DialogTitle>Delete Stack</DialogTitle>
              {deleteProcessing ? (
                <DialogContent>
                  <Progress />
                </DialogContent>
              ) : (
                <>
                  <DialogContent>
                    {deleteError && (
                      <Box marginBottom={2}>
                        <Alert severity="error">{deleteError}</Alert>
                      </Box>
                    )}
                    <DialogContentText>
                      Are you sure you want to delete the stack "{selectedStack.stack.name}" from environment "{selectedEnv.name}"?
                      This action cannot be undone.
                    </DialogContentText>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={handleDeleteDialogClose} color="default">
                      Cancel
                    </Button>
                    <Button onClick={handleDeleteStack} color="secondary" variant="contained">
                      Delete
                    </Button>
                  </DialogActions>
                </>
              )}
            </Dialog>
          </Box>
        ) : (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            style={{ height: '100%' }}
          >
            <Typography variant="body1" color="textSecondary">
              Select a stack from the sidebar to view details
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

